'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { isRoleAnalysisRunning, toasterApi } from '@terra/ui-design-system';
import { CUSTOM_ROLE_CREATE_DRAFT_ID } from '../../../utils/chatEditableObjectUtils';
import type { useTargetRoleActions } from '../target-settings/useTargetRoleActions';
import { useRoleCopilotEditor } from './useRoleCopilotEditor';

const ROLE_ANALYSIS_POLL_INTERVAL_MS = 3000;

type RoleFormData = {
  name: string;
  description?: string;
};

type CredentialWithRoleId = {
  roleId?: string | null;
};

export function useTargetCustomRoles({
  applicationId,
  roles,
  onRolesChange,
  roleActions,
  refetchRoles,
  credentials,
  isReadOnly,
}: {
  applicationId: string;
  roles: ApplicationRoleDto[];
  onRolesChange: (roles: ApplicationRoleDto[]) => void;
  roleActions: ReturnType<typeof useTargetRoleActions>;
  refetchRoles: () => Promise<unknown>;
  credentials: CredentialWithRoleId[];
  isReadOnly?: boolean;
}) {
  const tCommon = useTranslations('common');
  const tDsCommon = useTranslations('designSystem.common');
  const tCustomRoles = useTranslations('designSystem.credentials.customRoles');
  const tRoles = useTranslations('designSystem.applicationRoles');
  const editor = useRoleCopilotEditor({ applicationId, roles });
  const didSaveRef = useRef(false);
  const [roleToDelete, setRoleToDelete] = useState<ApplicationRoleDto | null>(
    null,
  );
  const [isDeletingRole, setIsDeletingRole] = useState(false);
  const { handleCreateRole, handleUpdateRole, handleDeleteRole } = roleActions;
  const { startCreating, startEditing } = editor;
  const editingRole = editor.isCreating
    ? undefined
    : (editor.resolvedEditingRole ?? undefined);
  const isRoleModalOpen =
    editor.isCreating || Boolean(editingRole && editor.roleForForm);
  const activeEditorRoleId = editor.isCreating
    ? CUSTOM_ROLE_CREATE_DRAFT_ID
    : editingRole?.id;
  const modalRole = editor.isCreating
    ? editor.createRoleForForm
    : editor.roleForForm;
  const modalFormKey = `${activeEditorRoleId ?? 'none'}:${editor.activeCustomRoleUpdateId ?? 'manual'}:${editor.formDraft.name}:${editor.formDraft.description}`;
  const hasRunningAnalysis = roles.some(isRoleAnalysisRunning);

  useEffect(() => {
    if (!hasRunningAnalysis) {
      return;
    }

    const interval = setInterval(() => {
      refetchRoles().catch((error: unknown) => {
        console.error(
          new Error('Failed to refresh roles while analysis is running'),
          error,
        );
      });
    }, ROLE_ANALYSIS_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasRunningAnalysis, refetchRoles]);

  const openCreateRoleModal = useCallback(() => {
    if (isReadOnly) {
      return;
    }

    startCreating();
  }, [isReadOnly, startCreating]);

  const openEditRoleModal = useCallback(
    (role: ApplicationRoleDto) => {
      if (isReadOnly) {
        return;
      }

      startEditing(role.id);
    },
    [isReadOnly, startEditing],
  );

  const closeRoleModal = () => {
    if (!activeEditorRoleId) {
      return;
    }

    if (didSaveRef.current) {
      didSaveRef.current = false;
      editor.closeEditor(activeEditorRoleId);

      return;
    }

    editor.cancelEditor(activeEditorRoleId);
  };

  const handleSaveRole = async ({ name, description }: RoleFormData) => {
    try {
      if (editingRole) {
        const updatedRole = await handleUpdateRole(editingRole.id, {
          name,
          description,
        });

        onRolesChange(
          roles.map((role) =>
            role.id === editingRole.id ? updatedRole : role,
          ),
        );
        toasterApi.success({ title: tCommon('success') });
      } else {
        const createdRole = await handleCreateRole({ name, description });

        onRolesChange([...roles, createdRole]);
        toasterApi.success({ title: tCustomRoles('createSuccess') });
      }

      didSaveRef.current = true;
    } catch (error) {
      console.error(new Error('Failed to save custom role'), error);
      toasterApi.error({ title: tDsCommon('error') });

      throw error;
    }
  };

  const requestDeleteRole = useCallback(
    (role: ApplicationRoleDto) => {
      if (isReadOnly) {
        return;
      }

      setRoleToDelete(role);
    },
    [isReadOnly],
  );

  const clearRoleToDelete = useCallback(() => setRoleToDelete(null), []);

  const confirmDeleteRole = async () => {
    if (!roleToDelete) {
      return;
    }

    const usageCount = credentials.filter(
      (credential) => credential.roleId === roleToDelete.id,
    ).length;

    if (usageCount > 0) {
      toasterApi.error({
        title: tRoles('toast.deleteError'),
        description: tRoles('toast.deleteErrorInUse', { count: usageCount }),
      });
      setRoleToDelete(null);

      return;
    }

    setIsDeletingRole(true);

    try {
      await handleDeleteRole(roleToDelete.id);
      onRolesChange(roles.filter((role) => role.id !== roleToDelete.id));
      editor.clearRoleEditState(roleToDelete.id);
      toasterApi.success({ title: tCustomRoles('deleteSuccess') });
      setRoleToDelete(null);
    } catch (error) {
      console.error(new Error('Failed to delete custom role'), error);
      toasterApi.error({ title: tDsCommon('error') });
    } finally {
      setIsDeletingRole(false);
    }
  };

  const analyzeRole = useCallback(
    async (roleId: string) => {
      try {
        return await roleActions.handleRoleAnalyze(roleId);
      } catch (error) {
        toasterApi.error({
          title: tRoles('toast.analyzeError'),
          description: error instanceof Error ? error.message : undefined,
        });

        throw error;
      }
    },
    [roleActions, tRoles],
  );

  const analyzeRoleFromList = useCallback(
    (role: ApplicationRoleDto) => {
      analyzeRole(role.id).catch((error: unknown) => {
        console.error(new Error('Failed to start role analysis'), error);
      });
    },
    [analyzeRole],
  );

  return {
    isRoleModalOpen,
    modalRole,
    modalFormKey,
    activeEditorRoleId,
    editingRoleLabel: editingRole?.name,
    pendingEditHandlers: editor.pendingEditHandlers,
    openCreateRoleModal,
    openEditRoleModal,
    closeRoleModal,
    handleSaveRole,
    roleToDelete,
    isDeletingRole,
    requestDeleteRole,
    clearRoleToDelete,
    confirmDeleteRole,
    analyzeRole,
    analyzeRoleFromList,
  };
}
